import React from 'react';
import { connect } from 'react-redux';
import { generatePath } from 'react-router';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import moment from 'moment';

import Button, { EditButton } from '@components/Button';

import {
  AGENCY_PRODUCT_CONFIGS_EDIT_SUCCESS,
  editAgencyProductConfigs,
  fetchAgencyCRMAuthentication,
  fetchAgencyProductConfigs,
  fetchAgencySettings,
} from '@actions/agency';
import { fetchRolesWithAgencyId } from '@actions/role';

import { generateAccountsURL, getAccountsEnabledStatus } from '@utils/AccountsUrlUtil';

import { portalConfigurationURL } from '../../../AdminApp';
import ProductCEForm from '../../ProductCEForm';
import ProductCRMForm from '../../ProductCRMForm';

import './style.scss';

export interface IProductConfigurationProps {
  agencySettings: IAgencySettings;
  productConfigurations: IProductConfigurationsResponse;
  crmAuthentication: IAgencyCRMAuthResponse;
  fetchAgencySettings: TFetchAgencySettings;
  fetchAgencyProductConfigs: TFetchAgencyProductConfigs;
  editAgencyProductConfigs: TEditAgencyProductConfigs;
  fetchAgencyCRMAuthentication: TFetchAgencyCRMAuthentication;
}

export interface IProductConfigurationState {
  productConfigurations: IProductConfigurationsResponse;
  isProductCEFormOpen: boolean;
  isProductCRMFormOpen: boolean;
}

export class ProductConfiguration extends React.Component<IProductConfigurationProps, IProductConfigurationState> {
  readonly timezoneOptions = [TimeZone.HST, TimeZone.AKST, TimeZone.PST, TimeZone.CST, TimeZone.EST, TimeZone.MST];

  constructor(props: IProductConfigurationProps) {
    super(props);
    this.state = this.getEmptyState();
  }

  componentDidMount() {
    this.props.fetchAgencyProductConfigs(this.props.agencySettings.id);
    this.props.fetchAgencyCRMAuthentication(this.props.agencySettings.id);
  }

  componentDidUpdate(prevProps: Readonly<IProductConfigurationProps>): void {
    if (prevProps.productConfigurations !== this.props.productConfigurations) {
      this.setState({
        productConfigurations: this.props.productConfigurations,
      });
    }
  }

  getEmptyState = (): IProductConfigurationState => ({
    productConfigurations: null,
    isProductCEFormOpen: false,
    isProductCRMFormOpen: false,
  });

  renderEmptyProductContainer = (product: string, onClick: () => void) => (
    <div onClick={onClick} className="empty-product">
      <i />
      {product}
    </div>
  );

  toggleProductCEForm = () => {
    this.setState((prevState) => ({
      isProductCEFormOpen: !prevState.isProductCEFormOpen,
    }));
  };

  renderActivatedCE = () => {
    const { xerce } = this.state.productConfigurations;
    const {
      isFineReceiptNumberYearIncluded,
      fineReceiptNumberPrefix,
      fineReceiptNumberInitialization,
      isCaseNumberYearIncluded,
      caseNumberPrefix,
      caseNumberInitialization,
      allowFineTracking,
      isMultiUnitPropertyEnabled,
    } = xerce;
    const fineReceiptNumberDisplayYear = isFineReceiptNumberYearIncluded ? `${moment().format('YY')}-` : '';
    const fineReceiptNumberDisplayPrefix = fineReceiptNumberPrefix ? `${fineReceiptNumberPrefix}-` : '';
    // tslint:disable-next-line:max-line-length
    const fineReceiptNumberDisplayInitialization = fineReceiptNumberInitialization
      ? `${fineReceiptNumberInitialization}`
      : '';
    const fineReceiptNumberDisplay =
      `${fineReceiptNumberDisplayPrefix}` +
      `${fineReceiptNumberDisplayYear}` +
      `${fineReceiptNumberDisplayInitialization}`;
    // Case Number
    const caseNumberDisplayYear = isCaseNumberYearIncluded ? `${moment().format('YY')}-` : '';
    const caseNumberDisplayPrefix = caseNumberPrefix ? `${caseNumberPrefix}-` : '';
    const caseNumberDisplayInitialization = caseNumberInitialization ? `${caseNumberInitialization}` : '';
    const caseNumberDisplay =
      `${caseNumberDisplayPrefix}` + `${caseNumberDisplayYear}` + `${caseNumberDisplayInitialization}`;

    return (
      <div className="product-container__product">
        <div className="product-header__title">
          <h4>{xerce.name}</h4>
          <h4
            className={'product-status' + `${xerce.isActive ? ' product-status--active' : ' product-status--inactive'}`}
          >
            {xerce.isActive ? 'Active' : 'Inactive'}
          </h4>
          <EditButton onClick={this.toggleProductCEForm} />
        </div>
        <div className="product-configs-container">
          <div className="product-config">
            <h5>Violation Type</h5>
            {xerce.violationTypes.map((violationType, index) => (
              <label
                key={index}
                className={`product-config__label ${
                  violationType.isActive ? 'product-config__label--active' : 'product-config__label--inactive'
                }`}
              >
                {`${violationType.label}: `}
                <span>{violationType.isActive ? 'On' : 'Off'}</span>
              </label>
            ))}
          </div>
          <div className="product-config">
            <h5>Product Modules</h5>
            <label className="product-config__label product-config__label--active">
              CDBG: <span>On</span>
            </label>
            <label
              className={`product-config__label ${
                allowFineTracking ? 'product-config__label--active' : 'product-config__label--inactive'
              }`}
            >
              Fines Tracking: <span>{allowFineTracking ? 'On' : 'Off'}</span>
            </label>
            <label className={'product-config__label' + ' product-config__label--active'}>
              Time Tracking: <span>On</span>
            </label>
            <label
              className={`product-config__label ${
                isMultiUnitPropertyEnabled ? 'product-config__label--active' : 'product-config__label--inactive'
              }`}
            >
              Multi-Unit Properties: <span>{isMultiUnitPropertyEnabled ? 'On' : 'Off'}</span>
            </label>
          </div>
          <div className="product-config">
            <h5>Case Naming</h5>
            <label>{caseNumberDisplay}</label>
          </div>
          <div className="product-config">
            <h5>Fine Receipt Naming</h5>
            <label>{fineReceiptNumberDisplay}</label>
          </div>
        </div>
      </div>
    );
  };

  toggleProductCRMForm = () => {
    this.setState((prevState) => ({
      isProductCRMFormOpen: !prevState.isProductCRMFormOpen,
    }));
  };

  renderActivatedCRM = () => {
    const {
      crm: {
        name,
        isCSNYearIncluded,
        CSNInitialization,
        CSNPrefix,
        isActive,
        token,
        isFAQEnabled,
        faqUrl,
        importEmailEnabled,
        importEmailId,
        citizenPortalHomeLinkURL,
      },
    } = this.state.productConfigurations;

    const CSNDisplayYear = isCSNYearIncluded ? `${moment().format('YY')}-` : '';
    const CSNDisplayPrefix = CSNPrefix ? `${CSNPrefix}-` : '';
    const CSNDisplayInitialization = CSNInitialization ? `${CSNInitialization}` : '';
    const CSNDisplay = `${CSNDisplayPrefix}` + `${CSNDisplayYear}` + `${CSNDisplayInitialization}`;

    const isAccountsEnabled = getAccountsEnabledStatus(this.props.crmAuthentication, undefined);
    const accountsURL = generateAccountsURL(token, citizenPortalHomeLinkURL, isAccountsEnabled);

    return (
      <div className={classnames('product-container__product')}>
        <div className={classnames('product-header__title')}>
          <h4>{name}</h4>
          <h4
            className={classnames('product-status', {
              'product-status--active': isActive,
              'product-status--inactive': !isActive,
            })}
          >
            {isActive ? 'Active' : 'Inactive'}
          </h4>
          <EditButton onClick={this.toggleProductCRMForm} />
        </div>
        <div className={classnames('product-configs-container')}>
          {accountsURL && (
            <div className={classnames('product-config')}>
              <h5 className={classnames('product-config__primary-label')}>Accounts URL</h5>
              <a
                href={accountsURL}
                target="_blank"
                className={classnames('product-config__label', 'product-config__label--primary')}
                rel="noreferrer"
              >
                {accountsURL}
              </a>
            </div>
          )}
          <div className={classnames('product-config')}>
            <h5
              className={classnames(accountsURL ? 'product-config__secondary-label' : 'product-config__primary-label')}
            >
              URL
            </h5>
            <a
              href={`${process.env.APP_HOST_URL}/crm/${token}`}
              target="_blank"
              className={classnames(
                'product-config__label',
                accountsURL ? 'product-config__label--secondary' : 'product-config__label--primary'
              )}
              rel="noreferrer"
            >
              {`${process.env.APP_HOST_URL}/crm/${token}`}
            </a>
          </div>
          <div className={classnames('product-config')}>
            <h5>Customer Submission Numbering</h5>
            <span className={classnames('product-config__label')}>{CSNDisplay}</span>
          </div>
          {isFAQEnabled && (
            <div className={classnames('product-config', 'management-section')}>
              <h5>FAQ Management</h5>
              <span>URL: </span> &nbsp;
              <a href={`//${faqUrl}`} target="_blank" className={classnames('product-config__label')} rel="noreferrer">
                {`${faqUrl}`}
              </a>
            </div>
          )}
          {importEmailEnabled && (
            <div className={classnames('product-config', 'management-section')}>
              <h5>Import Email</h5>
              <span>{importEmailId || ''}</span>
            </div>
          )}
          <div className={classnames('product-config', 'management-section')}>
            <h5>Citizen Portal</h5>
            <Link
              to={generatePath(portalConfigurationURL, { agency: this.props.agencySettings.id })}
              className={classnames('product-config__label')}
            >
              Configure portal
            </Link>
          </div>
        </div>
      </div>
    );
  };

  onProductCEFormSubmit = (e: React.SyntheticEvent<HTMLElement>, xerceRequest: IXerceResponse) => {
    this.setState((prevState) => ({
      productConfigurations: {
        ...prevState.productConfigurations,
        xerce: xerceRequest,
      },
      isProductCEFormOpen: false,
    }));
  };

  onProductCRMFormSubmit = (crm: ICRMRequest) => {
    const { hasActionCenterAccess } = this.props.agencySettings;

    this.setState((prevState) => ({
      productConfigurations: {
        ...prevState.productConfigurations,
        crm,
        hasActionCenterAccess: !crm.isActive ? false : hasActionCenterAccess,
      },
      isProductCRMFormOpen: false,
    }));
  };

  convertXerceResponseToRequest = (xerce: IXerceResponse): IXerceRequest => {
    const violationTypes = xerce.violationTypes.map((violationType) => ({
      systemXerceViolationTypeId: violationType.systemXerceViolationTypeId,
      isActive: violationType.isActive,
    }));
    const isFineReceiptNumberResettable = xerce.isFineReceiptNumberYearIncluded
      ? xerce.isFineReceiptNumberResettable
      : false;
    const isCaseNumberResettable = xerce.isCaseNumberYearIncluded ? xerce.isCaseNumberResettable : false;
    const allowFineTracking = xerce.allowFineTracking;
    const caseNumberPrefix = xerce.caseNumberPrefix || '';
    const fineReceiptNumberPrefix = xerce.fineReceiptNumberPrefix || '';

    return {
      ...xerce,
      caseNumberPrefix,
      fineReceiptNumberPrefix,
      isCaseNumberResettable,
      isFineReceiptNumberResettable,
      violationTypes,
      allowFineTracking,
    };
  };

  getRequestFromState = (): IAgencyProductConfigEditRequest => {
    const { crm, xerce } = this.state.productConfigurations;

    const agencyRequest: IAgencyProductConfigEditRequest = {};

    if (xerce) {
      agencyRequest.xerce = this.convertXerceResponseToRequest(xerce);
    }
    if (crm) {
      agencyRequest.crm = crm;
    }

    return agencyRequest;
  };

  onSubmit = () => {
    const { id: agencyId } = this.props.agencySettings;
    const editRequest = this.getRequestFromState();

    this.props.editAgencyProductConfigs(agencyId, editRequest).then((action) => {
      if (action.type === AGENCY_PRODUCT_CONFIGS_EDIT_SUCCESS) {
        this.props.fetchAgencySettings(agencyId);
        this.props.fetchAgencyProductConfigs(agencyId);
        this.props.fetchAgencyCRMAuthentication(agencyId);
      }
    });
  };

  render() {
    const { xerce, crm } = this.state.productConfigurations ?? {};

    return (
      <div className="agency-products-listing">
        <div className="product-container">
          {xerce
            ? this.renderActivatedCE()
            : this.renderEmptyProductContainer('Code Enforcement', this.toggleProductCEForm)}
        </div>
        <div className="product-container">
          {crm ? this.renderActivatedCRM() : this.renderEmptyProductContainer('CRM', this.toggleProductCRMForm)}
        </div>

        <div className="agency-setting__footer">
          <Button bsStyle={ButtonStyle.DEFAULT}>Cancel</Button>
          <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit}>
            Save Changes
          </Button>
        </div>

        <ProductCEForm
          xerce={this.state.productConfigurations?.xerce}
          show={this.state.isProductCEFormOpen}
          onClose={this.toggleProductCEForm}
          // to be handled with integrations
          onDisableFineTracking={null}
          onSubmit={this.onProductCEFormSubmit}
        />
        <ProductCRMForm
          crm={this.state.productConfigurations?.crm}
          show={this.state.isProductCRMFormOpen}
          onClose={this.toggleProductCRMForm}
          onSubmit={this.onProductCRMFormSubmit}
          agencyName={this.props.agencySettings.name.replace(/ /g, '')}
          agencyId={this.props.agencySettings.id}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agencySettings: state.agency.agencySettings,
  productConfigurations: state.agency.productConfigurations,
  crmAuthentication: state.agency.crmAuthentication,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAgencySettings: (agencyId: number) => dispatch(fetchAgencySettings(agencyId)),
  fetchAgencyProductConfigs: (agencyId: number) => dispatch(fetchAgencyProductConfigs(agencyId)),
  fetchAgencyCRMAuthentication: (agencyId: number) => dispatch(fetchAgencyCRMAuthentication(agencyId)),
  fetchRoles: (agencyId: number) => dispatch(fetchRolesWithAgencyId(agencyId)),
  editAgencyProductConfigs: (agencyId: number, editRequest: IAgencyProductConfigEditRequest) =>
    dispatch(editAgencyProductConfigs(agencyId, editRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductConfiguration);
